/* eslint-disable @typescript-eslint/no-explicit-any */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function assertAdmin(supabase: any, userId: string) {
  const { data } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (!data) throw new Error("Forbidden: admin access required");
}

export type AttemptRow = {
  id: string;
  buyerName: string;
  buyerEmail: string | null;
  buyerPhone: string | null;
  planName: string;
  amount: number;
  status: "paid" | "failed" | "created";
  orderId: string | null;
  paymentId: string | null;
  interviewDate: string | null;
  createdAt: string;
  isManual: boolean;
  statusReason: string;
};

const isManualOrder = (id: string | null | undefined) =>
  !id || id.toLowerCase().startsWith("manual");

/** Human-readable reason for why an attempt is not a completed payment. */
export function deriveStatusReason(row: {
  status: string | null;
  failure_reason: string | null;
  notes: string | null;
  razorpay_order_id: string | null;
  razorpay_payment_id: string | null;
  created_at: string;
}): string {
  const manual = isManualOrder(row.razorpay_order_id);
  if (row.status === "paid") {
    return manual ? "Manual Payment: Verified" : "Captured by Razorpay";
  }
  if (row.status === "failed") {
    const raw = (row.failure_reason ?? "").trim();
    if (!raw) return "Payment Authorization Failed";
    const l = raw.toLowerCase();
    if (l.includes("bank") || l.includes("gateway")) return `Bank Server Down — ${raw}`;
    return raw;
  }
  // created / pending
  if (manual) return "Manual Payment: Pending Verification";
  if (row.razorpay_payment_id) return "Authorized — awaiting capture";
  const ageMin = (Date.now() - new Date(row.created_at).getTime()) / 60000;
  if (ageMin > 60) return "Abandoned by User";
  return "Checkout in progress";
}

function toAttempt(p: any): AttemptRow {
  return {
    id: p.id,
    buyerName: p.buyer_name || "Unknown",
    buyerEmail: p.buyer_email ?? null,
    buyerPhone: p.buyer_phone ?? null,
    planName: p.plan_name || "—",
    amount: (p.amount_paise || 0) / 100,
    status: p.status === "paid" ? "paid" : p.status === "failed" ? "failed" : "created",
    orderId: p.razorpay_order_id ?? null,
    paymentId: p.razorpay_payment_id ?? null,
    interviewDate: p.interview_date ?? null,
    createdAt: p.created_at,
    isManual: isManualOrder(p.razorpay_order_id),
    statusReason: deriveStatusReason(p),
  };
}

/**
 * Every raw checkout attempt — NO deduplication, NO grouping.
 * Returns pending/failed attempts plus attempts that were recently cleared,
 * so admins can trace the full funnel row by row.
 */
export const listPaymentAttempts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("payments")
      .select(
        "id, plan_name, amount_paise, status, buyer_name, buyer_phone, buyer_email, interview_date, razorpay_order_id, razorpay_payment_id, failure_reason, notes, created_at",
      )
      .order("created_at", { ascending: false })
      .limit(1000);
    if (error) throw new Error(error.message);
    return { attempts: (data ?? []).map(toAttempt) };
  });

/** Manually clear a pending/failed attempt as paid (offline / UPI transfers). */
export const markPaymentVerified = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { paymentId: string; note?: string }) =>
    z.object({ paymentId: z.string().uuid(), note: z.string().max(300).optional() }).parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const stamp = new Date().toISOString().slice(0, 10);
    const note = `Manually verified by admin on ${stamp}${data.note ? ` — ${data.note}` : ""}`;
    const { error } = await context.supabase
      .from("payments")
      .update({ status: "paid", failure_reason: null, notes: note } as never)
      .eq("id", data.paymentId);
    if (error) throw new Error(error.message);
    return { ok: true, note };
  });

/** Re-query Razorpay for a single order and repair its stored status. */
export const recheckRazorpayStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { paymentId: string }) =>
    z.object({ paymentId: z.string().uuid() }).parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);

    const { data: row, error } = await context.supabase
      .from("payments")
      .select("id, razorpay_order_id, status")
      .eq("id", data.paymentId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) throw new Error("Attempt not found");
    const orderId = (row as any).razorpay_order_id as string | null;
    if (!orderId || !orderId.startsWith("order_")) {
      return { ok: false, status: (row as any).status as string, message: "No Razorpay order to check (manual entry)." };
    }

    const keyId = process.env['RAZORPAY_KEY_ID'];
    const keySecret = process.env['RAZORPAY_KEY_SECRET'];
    if (!keyId || !keySecret) throw new Error("Razorpay keys are not configured");

    const res = await fetch(`https://api.razorpay.com/v1/orders/${orderId}/payments`, {
      headers: { Authorization: "Basic " + Buffer.from(`${keyId}:${keySecret}`).toString("base64") },
    });
    if (!res.ok) throw new Error(`Razorpay lookup failed (${res.status})`);
    const payload = (await res.json()) as { items?: Array<{ id: string; status: string; error_description?: string }> };
    const items = payload.items ?? [];
    const captured = items.find((p) => p.status === "captured" || p.status === "authorized");
    const failed = items.find((p) => p.status === "failed");

    if (!captured && !failed) {
      return { ok: true, status: (row as any).status as string, message: "Razorpay has no attempt recorded for this order yet." };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const nextStatus = captured ? "paid" : "failed";
    await supabaseAdmin
      .from("payments")
      .update({
        status: nextStatus,
        razorpay_payment_id: (captured ?? failed)?.id ?? null,
        failure_reason: captured ? null : failed?.error_description ?? "Payment Authorization Failed",
      } as never)
      .eq("id", data.paymentId);

    if (captured) {
      try {
        const { sendPaymentConfirmation } = await import("@/lib/email/payment-confirmation.server");
        await sendPaymentConfirmation({ razorpayOrderId: orderId });
      } catch (err) {
        console.error("recheck confirmation email failed:", err);
      }
    }

    return {
      ok: true,
      status: nextStatus,
      message: captured ? "Recovered as paid" : "Confirmed failed at Razorpay",
    };
  });

// -------- Global student detail editing ----------------------------------

const detailsSchema = z.object({
  /** Identity to match on (existing values). */
  matchEmail: z.string().trim().max(200).optional().nullable(),
  matchPhone: z.string().trim().max(40).optional().nullable(),
  matchPaymentId: z.string().uuid().optional().nullable(),
  fullName: z.string().trim().min(1).max(200),
  email: z.string().trim().email().max(200).optional().nullable(),
  phone: z.string().trim().max(40).optional().nullable(),
  biometricsDate: z.string().trim().max(20).optional().nullable(),
  visaDate: z.string().trim().max(20).optional().nullable(),
  plan: z.string().trim().max(120).optional().nullable(),
});

const isoDate = (v: string | null | undefined) => (v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null);

/**
 * Updates a student's core details everywhere at once: every payment row that
 * belongs to them plus their intake record. Admin surfaces re-read from these
 * two tables, so a single save keeps every tracker in sync.
 */
export const updateStudentDetailsGlobal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: z.infer<typeof detailsSchema>) => detailsSchema.parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);

    const matchEmail = (data.matchEmail ?? "").toLowerCase().trim();
    const matchDigits = (data.matchPhone ?? "").replace(/\D/g, "").slice(-10);
    const email = (data.email ?? "").toLowerCase().trim() || null;
    const visaDate = isoDate(data.visaDate);
    const biometricsDate = isoDate(data.biometricsDate);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // ---- payments -------------------------------------------------------
    const paymentPatch: Record<string, unknown> = {
      buyer_name: data.fullName,
      buyer_email: email,
      interview_date: visaDate,
    };
    if (data.phone) paymentPatch['buyer_phone'] = data.phone;
    if (data.plan) paymentPatch['plan_name'] = data.plan;

    let paymentsUpdated = 0;
    const targetIds = new Set<string>();
    if (data.matchPaymentId) targetIds.add(data.matchPaymentId);

    if (matchEmail) {
      const { data: byEmail } = await supabaseAdmin
        .from("payments")
        .select("id")
        .eq("buyer_email", matchEmail);
      (byEmail ?? []).forEach((r: any) => targetIds.add(r.id));
    }
    if (!matchEmail && matchDigits) {
      const { data: byPhone } = await supabaseAdmin
        .from("payments")
        .select("id, buyer_phone")
        .like("buyer_phone", `%${matchDigits}`);
      (byPhone ?? []).forEach((r: any) => targetIds.add(r.id));
    }

    if (targetIds.size > 0) {
      const { error: payErr } = await supabaseAdmin
        .from("payments")
        .update(paymentPatch as never)
        .in("id", Array.from(targetIds));
      if (payErr) throw new Error(payErr.message);
      paymentsUpdated = targetIds.size;
    }

    // ---- client intakes --------------------------------------------------
    let intakesUpdated = 0;
    const intakePatch: Record<string, unknown> = {
      full_name: data.fullName,
      visa_date: visaDate,
      biometrics_date: biometricsDate,
    };
    if (email) intakePatch['email'] = email;
    if (data.phone) intakePatch['whatsapp'] = data.phone;
    if (data.plan) intakePatch['plan'] = data.plan;

    if (matchEmail) {
      const { data: intakes } = await supabaseAdmin
        .from("client_intakes")
        .select("id")
        .eq("email", matchEmail);
      const ids = (intakes ?? []).map((r: any) => r.id);
      if (ids.length) {
        const { error: inErr } = await supabaseAdmin
          .from("client_intakes")
          .update(intakePatch as never)
          .in("id", ids);
        if (inErr) throw new Error(inErr.message);
        intakesUpdated = ids.length;
      }
    }

    return { ok: true, paymentsUpdated, intakesUpdated };
  });

const deleteSchema = z.object({
  /** Delete only these payment attempt rows (duplicate transactions). */
  paymentIds: z.array(z.string().uuid()).max(50).optional(),
  /** When true, purge the whole student record (all payments + intakes). */
  purgeStudent: z.boolean().optional(),
  matchEmail: z.string().trim().max(200).optional().nullable(),
  matchPhone: z.string().trim().max(40).optional().nullable(),
});

/**
 * Permanently removes duplicate records from the admin console.
 * Either a specific set of payment attempts, or the student's entire footprint
 * (every payment row + their intake submissions) when `purgeStudent` is set.
 */
export const deleteStudentRecords = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: z.infer<typeof deleteSchema>) => deleteSchema.parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const ids = new Set<string>(data.paymentIds ?? []);
    const email = (data.matchEmail ?? "").toLowerCase().trim();
    const digits = (data.matchPhone ?? "").replace(/\D/g, "").slice(-10);

    let intakesDeleted = 0;

    if (data.purgeStudent) {
      if (email) {
        const { data: rows } = await supabaseAdmin.from("payments").select("id").eq("buyer_email", email);
        (rows ?? []).forEach((r: any) => ids.add(r.id));
      }
      if (digits) {
        const { data: rows } = await supabaseAdmin
          .from("payments")
          .select("id")
          .like("buyer_phone", `%${digits}`);
        (rows ?? []).forEach((r: any) => ids.add(r.id));
      }
      if (email) {
        const { data: intakes } = await supabaseAdmin.from("client_intakes").select("id").eq("email", email);
        const intakeIds = (intakes ?? []).map((r: any) => r.id);
        if (intakeIds.length) {
          await supabaseAdmin.from("visa_approvals").delete().in("client_intake_id", intakeIds);
          const { error } = await supabaseAdmin.from("client_intakes").delete().in("id", intakeIds);
          if (error) throw new Error(error.message);
          intakesDeleted = intakeIds.length;
        }
      }
    }

    let paymentsDeleted = 0;
    const idList = Array.from(ids);
    if (idList.length) {
      // Detach dependent admin rows so the delete never fails on a FK.
      await supabaseAdmin.from("admin_student_milestones").delete().in("payment_id", idList);
      await supabaseAdmin.from("ds160_submissions").update({ payment_id: null } as never).in("payment_id", idList);
      const { error } = await supabaseAdmin.from("payments").delete().in("id", idList);
      if (error) throw new Error(error.message);
      paymentsDeleted = idList.length;
    }

    return { ok: true, paymentsDeleted, intakesDeleted };
  });
